import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { wishlistAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Heart, Search, SortAsc, Trash2, ShoppingCart, AlertTriangle, Loader2, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';

export default function Wishlist() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [wishlist, setWishlist] = useState([]);
  const [stats, setStats] = useState({ total_items: 0, total_value: null });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('added');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Move to collection dialog
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [moveConditionVinyl, setMoveConditionVinyl] = useState('VG+');
  const [moveConditionCover, setMoveConditionCover] = useState('VG+');
  const [moving, setMoving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [wishlistRes, statsRes] = await Promise.all([
        wishlistAPI.getAll({ sort_by: sortBy, sort_order: sortOrder }),
        wishlistAPI.getStats()
      ]);
      setWishlist(wishlistRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Could not fetch wishlist');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemove = async (itemId) => {
    try {
      await wishlistAPI.remove(itemId);
      setWishlist(prev => prev.filter(item => item.id !== itemId));
      toast.success('Removed from wishlist');
    } catch (err) {
      toast.error('Could not remove from wishlist');
    }
  };

  const openMoveDialog = (item) => {
    setSelectedItem(item);
    setMoveConditionVinyl('VG+');
    setMoveConditionCover('VG+');
    setShowMoveDialog(true);
  };

  const handleMoveToCollection = async () => {
    if (!selectedItem) return;
    
    setMoving(true);
    try {
      await wishlistAPI.moveToCollection(selectedItem.id, moveConditionVinyl, moveConditionCover);
      setWishlist(prev => prev.filter(item => item.id !== selectedItem.id));
      setShowMoveDialog(false);
      toast.success('Moved to collection!');
      // Update stats
      const statsRes = await wishlistAPI.getStats();
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Could not move to collection');
    } finally {
      setMoving(false);
    }
  };

  // Filter by search
  const filteredWishlist = wishlist.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.artist?.toLowerCase().includes(query) ||
      item.title?.toLowerCase().includes(query)
    );
  });

  const conditionOptions = ['M', 'NM', 'VG+', 'VG', 'G+', 'G', 'F', 'P'];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Heart className="w-6 h-6 text-pink-500" />
              <div>
                <h1 className="text-xl font-bold">Wishlist</h1>
                <p className="text-xs text-muted-foreground">
                  {stats.total_items} albums {stats.total_value && `• ${formatCurrency(stats.total_value, stats.currency)}`}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search wishlist..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-secondary border-border"
            />
          </div>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px] h-10 bg-secondary border-border">
              <SortAsc className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="added">Added</SelectItem>
              <SelectItem value="artist">Artist</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="year">Year</SelectItem>
              <SelectItem value="value">Value</SelectItem>
              <SelectItem value="priority">Priority</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            <SortAsc className={`w-4 h-4 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Wishlist */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredWishlist.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {searchQuery ? 'Ingen resultater' : 'Your wishlist is empty'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery ? 'Try a different search' : 'Add albums you want from search results'}
            </p>
            <Button onClick={() => navigate('/')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to collection
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredWishlist.map((item) => (
              <div
                key={item.id}
                className={`flex items-center gap-4 p-4 rounded-lg bg-card border transition-colors ${
                  item.already_owned ? 'border-amber-500/50' : 'border-border'
                }`}
              >
                {/* Cover */}
                <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0 bg-secondary">
                  {item.cover_image_url ? (
                    <img
                      src={item.cover_image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Heart className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{item.title}</h3>
                      <p className="text-sm text-muted-foreground truncate">{item.artist}</p>
                    </div>
                    {item.already_owned && (
                      <Badge variant="outline" className="flex-shrink-0 border-amber-500 text-amber-500 gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Already owned
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {item.year && <Badge variant="secondary" className="text-xs">{item.year}</Badge>}
                    {item.label && <span className="text-xs text-muted-foreground">{item.label}</span>}
                    {item.country && <span className="text-xs text-muted-foreground">• {item.country}</span>}
                  </div>
                </div>

                {/* Value */}
                <div className="text-right hidden sm:block">
                  <p className="font-medium">
                    {item.market_value_median 
                      ? formatCurrency(item.market_value_median, item.market_value_currency)
                      : '-'}
                  </p>
                  {item.market_value_low && (
                    <p className="text-xs text-muted-foreground">
                      fra {formatCurrency(item.market_value_low, item.market_value_currency)}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => openMoveDialog(item)}
                    className="gap-1"
                  >
                    <ShoppingCart className="w-3 h-3" />
                    <span className="hidden sm:inline">Add</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemove(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Move to Collection Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Move to Collection</DialogTitle>
            <DialogDescription>
              Select condition for "{selectedItem?.title}"
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem?.already_owned && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">You already own this release. Add anyway?</p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Vinyl Condition</label>
              <Select value={moveConditionVinyl} onValueChange={setMoveConditionVinyl}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {conditionOptions.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Cover Condition</label>
              <Select value={moveConditionCover} onValueChange={setMoveConditionCover}>
                <SelectTrigger className="bg-secondary border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {conditionOptions.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Cancel</Button>
            <Button onClick={handleMoveToCollection} disabled={moving} className="gap-2">
              {moving && <Loader2 className="w-4 h-4 animate-spin" />}
              <ShoppingCart className="w-4 h-4" />
              Add to collection
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
